"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import log from "logbeacon";
import { OspToast } from "@ospweb/sdk-ui";
import { cn, safeStringifyError } from "utils";
import {
  trackGiftGoalSetupDone,
  trackPutEdToWorkTap,
} from "@/src/hooks/onboarding/onboardingAnalytics";
import { getOnboardingV3Session } from "@/src/hooks/onboarding/onboardingV3SessionStorage";
import {
  clearSaveGoalPlannedActivateGoalId,
  getSaveGoalPlannedActivateGoalId,
  persistSaveGoalPlannedActivateGoalId,
} from "@/src/hooks/onboarding/saveGoalPlannedActivateSession";
import { resolveGoalIdForActivate } from "@/src/hooks/goal/resolveGoalIdForActivate";
import { trackPlanSetGoalDone } from "@/src/screens/plan/planActivityAnalytics";
import { SaveGoalCardGoalSection } from "./SaveGoalCardGoalPreview";
import { SaveGoalCardPlanPreviewSection } from "./SaveGoalCardPlanPreviewSection";
import { hasSaveGoalResult } from "./parseSaveGoalResult";
import type {
  InteractiveToolInfo,
  SaveGoalArgs,
  SaveGoalCardSubmitPayload,
} from "./types";
import { useMemoizedFn } from "ahooks";
import { normalizeSaveGoalTrackerSpecs } from "./saveGoalTrackerSpecs";
import { SAVE_GOAL_ACTIVATE_COPY } from "./saveGoalActivateCardCopy";
import {
  closePlanUnlockedMovesModal,
  openPlanUnlockedMovesModal,
} from "./PlanUnlockedMovesModal";
import { OnboardingEmphasisCtaButton } from "@/src/components/OnboardingWizard/shared/OnboardingEmphasisCtaButton";
import { useGoalActivatePaywallGate } from "@/src/hooks/goal/useGoalActivatePaywallGate";
import { GoalConfirmScoreImpact } from "./GoalConfirmScoreImpact";
import { useFitnessScore } from "@/src/hooks/fitness";

/** 复用既有 i18n key 文案（translate 只读，不新增 key） */
const ACTIVATE_FAILED_TOAST = "Something went wrong. Please try again.";
const ACTIVATE_FAILED_TOAST_KEY = "save-goal-activate-failed";

export interface SaveGoalCardProps {
  args: SaveGoalArgs;
  /** interactiveToolCall.result；有值时不展示 CTA */
  result?: any;
  toolInfo?: InteractiveToolInfo;
  /** 当前会话 id；Active my plan 成功后用于刷新 chat detail */
  chatId?: string;
  className?: string;
  /** 提交中禁用按钮，避免重复 confirm */
  disabled?: boolean;
  onSubmit?: (payload: SaveGoalCardSubmitPayload) => void | Promise<void>;
}

/**
 * save_goal Activate goal 卡片（Figma 96138:33273 · UI14）。
 */
export const SaveGoalCard: React.FC<SaveGoalCardProps> = ({
  args,
  result,
  toolInfo,
  chatId,
  className,
  disabled = false,
  onSubmit,
}) => {
  const hasResult = hasSaveGoalResult(result);
  const toolCallId = toolInfo?.toolCallId;
  const plannedGoalIdFromSession = getSaveGoalPlannedActivateGoalId(toolCallId);
  const [pendingPlannedGoalId, setPendingPlannedGoalId] = useState<
    string | undefined
  >(() => plannedGoalIdFromSession);
  const hasConfirmedSave =
    hasResult || Boolean(pendingPlannedGoalId ?? plannedGoalIdFromSession);
  const { runActivate, isActivating } = useGoalActivatePaywallGate();
  const { data: fitnessScore } = useFitnessScore();

  const showGoalImpact = args.impact != null && args.impact.delta > 0;

  const moves = useMemo(
    () => normalizeSaveGoalTrackerSpecs(args.trackerSpecs),
    [args.trackerSpecs],
  );
  const hasMoves = moves.length > 0;
  const isBusy = disabled || isActivating;
  const showInteractiveActions = !hasResult;
  const isCardClickable = hasResult && hasMoves;

  useEffect(() => {
    if (!showInteractiveActions || !hasMoves) {
      return;
    }
    const onboardingDraftId = getOnboardingV3Session()?.planPreview?.draftId;
    const isOnboardingGiftHandoff =
      Boolean(onboardingDraftId) &&
      (!args.goalId || args.goalId === onboardingDraftId);
    if (isOnboardingGiftHandoff) {
      trackGiftGoalSetupDone();
      return;
    }
    trackPlanSetGoalDone();
  }, [args.goalId, hasMoves, showInteractiveActions]);

  const showActivateFailedToast = useMemoizedFn(() => {
    OspToast.error({
      content: ACTIVATE_FAILED_TOAST,
      key: ACTIVATE_FAILED_TOAST_KEY,
    });
  });

  const handleActivatePlan = useMemoizedFn(async () => {
    if (isBusy) {
      return;
    }

    trackPutEdToWorkTap("put_ed_to_work");

    const resolvedGoalId = resolveGoalIdForActivate(
      args,
      pendingPlannedGoalId ?? plannedGoalIdFromSession,
    );

    try {
      const activateResult = await runActivate({
        entry: "chat_active_my_plan",
        saveGoalArgs: args,
        hasConfirmSaveResult: hasConfirmedSave,
        goalId: resolvedGoalId,
        chatId,
      });

      if (activateResult.outcome === "dismissed") {
        return;
      }

      if (activateResult.outcome === "missing_goal_id") {
        log.warn("[SaveGoalCard] activate skipped — missing goalId");
        showActivateFailedToast();
        return;
      }

      if (activateResult.outcome === "confirmed") {
        closePlanUnlockedMovesModal();
        if (
          activateResult.status === "PLANNED" &&
          activateResult.goalId?.trim()
        ) {
          // PLANNED：保留 CTA，二次 Paywall 再 activate；session 记下 goalId
          const plannedGoalId = activateResult.goalId;
          setPendingPlannedGoalId(plannedGoalId);
          if (toolCallId) {
            persistSaveGoalPlannedActivateGoalId(toolCallId, plannedGoalId);
          }
        } else {
          // ACTIVE：已有入口 goalId 时不 append（靠 chat detail / 既有 result）；
          // 无 goalId 创建分支才 append confirmed，隐藏 CTA
          setPendingPlannedGoalId(undefined);
          clearSaveGoalPlannedActivateGoalId(toolCallId);
          if (!resolvedGoalId) {
            await onSubmit?.({ type: "confirmed" });
          }
        }
        return;
      }

      if (activateResult.outcome === "activated") {
        // activate 路径必有 goalId，不 append tool result
        closePlanUnlockedMovesModal();
        setPendingPlannedGoalId(undefined);
        clearSaveGoalPlannedActivateGoalId(toolCallId);
        return;
      }

      log.error(
        "[SaveGoalCard] activate flow failed",
        safeStringifyError(activateResult.error),
      );
      showActivateFailedToast();
    } catch (error) {
      log.error(
        "[SaveGoalCard] activate flow failed",
        safeStringifyError(error),
      );
      showActivateFailedToast();
    }
  });

  const openReadonlyModal = useCallback(() => {
    if (isBusy || !hasMoves) {
      return;
    }

    void openPlanUnlockedMovesModal({
      args,
      mode: "readonly",
    });
  }, [args, hasMoves, isBusy]);

  const openInteractiveModal = useCallback(() => {
    if (isBusy || !hasMoves) {
      return;
    }

    void openPlanUnlockedMovesModal({
      args,
      mode: "interactive",
      isActivating,
      onActivate: handleActivatePlan,
    });
  }, [args, handleActivatePlan, hasMoves, isBusy, isActivating]);

  const handleCardClick = () => {
    if (!isCardClickable || isBusy) {
      return;
    }
    openReadonlyModal();
  };

  return (
    <div
      className={cn(
        "relative flex w-full max-w-[361px] flex-col overflow-hidden rounded-xl border border-border_inverse_transparent10 bg-background_solid_neutral_primary",
        isCardClickable && "cursor-pointer",
        className,
      )}
      data-testid="save-goal-card"
      data-tool-name={toolInfo?.toolName}
      data-tool-call-id={toolInfo?.toolCallId}
      onClick={isCardClickable ? handleCardClick : undefined}
    >
      <SaveGoalCardGoalSection args={args} layout="activate" />

      <div
        className="relative z-[1] flex flex-col gap-6 bg-background_solid_neutral_primary p-5"
        data-testid="save-goal-card-body"
      >
        {showGoalImpact ? (
          <GoalConfirmScoreImpact
            impact={args.impact}
            fitnessScore={fitnessScore ?? null}
            showRadar={Boolean(fitnessScore)}
          />
        ) : null}

        {hasMoves ? (
          <SaveGoalCardPlanPreviewSection
            moves={moves}
            readOnly={hasResult}
            onViewDetails={
              showInteractiveActions ? openInteractiveModal : undefined
            }
          />
        ) : null}

        {showInteractiveActions ? (
          <OnboardingEmphasisCtaButton
            testId="save-goal-card-activate-cta"
            label={SAVE_GOAL_ACTIVATE_COPY.activateCta}
            disabled={isBusy}
            loading={isActivating}
            sizeClassName="button_m"
            className="rounded-lg"
            onClick={(event) => {
              event.stopPropagation();
              void handleActivatePlan();
            }}
          />
        ) : null}
      </div>
    </div>
  );
};
